import { lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Routes,
  Route,
  useLocation
} from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import HomePage from '@/features/Home/HomePage';
import AppNavbar from '@/shared/components/AppNavbar';
import { BackToTopButton } from '@/shared/components/BackToTopButton';
import { useI18n } from '@/shared/i18n';
import {
  applySeoMetadata,
  createAbsoluteUrl,
  openGraphLocaleByLanguage
} from '@/shared/seo';
import { NAVIGATION_EVENTS } from '@/shared/utils/constants';
import { routeTransition } from '@/shared/utils/motion';

const AboutPage = lazy(() => import('@/features/About/AboutPage'));
const CasesPage = lazy(() => import('@/features/Cases/CasesPage'));
const ContactPage = lazy(() => import('@/features/Contact/ContactPage'));
const InsightArticlePage = lazy(
  () => import('@/features/Insights/InsightArticlePage')
);
const InsightsPage = lazy(() => import('@/features/Insights/InsightsPage'));
const PartnersPage = lazy(() => import('@/features/Partners/PartnersPage'));
const AiMachineLearningPage = lazy(
  () => import('@/features/Services/pages/AiMachineLearningPage')
);
const BusinessIntelligencePage = lazy(
  () => import('@/features/Services/pages/BusinessIntelligencePage')
);
const DataEngineeringPage = lazy(
  () => import('@/features/Services/pages/DataEngineeringPage')
);
const ManagedDataPlatformPage = lazy(
  () => import('@/features/Services/pages/ManagedDataPlatformPage')
);
const NotFoundPage = lazy(() => import('@/features/NotFound/NotFoundPage'));

const routeScrollContainerSelector = '[data-route-scroll-container]';

const normalizePathname = (pathname: string) => {
  const normalizedPathname = pathname.replace(/\/+$/, '');

  return normalizedPathname || '/';
};

const getPreferredScrollBehavior = (): ScrollBehavior => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return 'auto';
  }

  return 'smooth';
};

const scrollRouteToTop = (behavior: ScrollBehavior = 'auto') => {
  window.scrollTo({ top: 0, left: 0, behavior });

  document
    .querySelectorAll<HTMLElement>(routeScrollContainerSelector)
    .forEach(scrollContainer => {
      scrollContainer.scrollTo({ top: 0, left: 0, behavior });
    });
};

const dispatchHomeTopNavigation = () => {
  window.dispatchEvent(
    new CustomEvent(NAVIGATION_EVENTS.HOME_SECTION_CHANGE, {
      detail: { sectionIndex: 0 }
    })
  );
};

const getCanonicalPathname = (pathname: string) => {
  if (pathname.startsWith('/blog/')) {
    return pathname.replace(/^\/blog/, '/insights');
  }

  const aliases: Record<string, string> = {
    '/blog': '/insights',
    '/o-nama': '/about',
    '/partneri': '/partners',
    '/kontakt': '/contact'
  };

  return aliases[pathname] ?? pathname;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const resetScrollPosition = () => scrollRouteToTop('auto');

    resetScrollPosition();

    const animationFrame = window.requestAnimationFrame(resetScrollPosition);

    return () => window.cancelAnimationFrame(animationFrame);
  }, [pathname]);

  return null;
};

const SameRouteScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const handleSameRouteLinkClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      ) {
        return;
      }

      if (!(event.target instanceof Element)) return;

      const link = event.target.closest('a[href]');

      if (!(link instanceof HTMLAnchorElement)) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;

      let targetUrl: URL;

      try {
        targetUrl = new URL(link.href, window.location.href);
      } catch {
        return;
      }

      if (targetUrl.origin !== window.location.origin) return;
      if (targetUrl.hash) return;

      const currentPathname = normalizePathname(pathname);
      const targetPathname = normalizePathname(targetUrl.pathname);

      if (currentPathname !== targetPathname) return;

      window.requestAnimationFrame(() => {
        if (targetPathname === '/') {
          dispatchHomeTopNavigation();
        }

        scrollRouteToTop(getPreferredScrollBehavior());
      });
    };

    document.addEventListener('click', handleSameRouteLinkClick, true);

    return () => {
      document.removeEventListener('click', handleSameRouteLinkClick, true);
    };
  }, [pathname]);

  return null;
};

const RouteSeo = () => {
  const { pathname } = useLocation();
  const { language, t } = useI18n();

  useEffect(() => {
    const pageTitles: Record<string, string> = {
      '/': t.pageTitles.home,
      '/services/data-engineering': t.pageTitles.dataEngineering,
      '/services/managed-data-platform': t.pageTitles.managedDataPlatform,
      '/services/business-intelligence': t.pageTitles.businessIntelligence,
      '/services/ai-machine-learning': t.pageTitles.aiMachineLearning,
      '/cases': t.pageTitles.cases,
      '/insights': t.pageTitles.insights,
      '/blog': t.pageTitles.insights,
      '/about': t.pageTitles.about,
      '/o-nama': t.pageTitles.about,
      '/partners': t.pageTitles.partners,
      '/partneri': t.pageTitles.partners,
      '/contact': t.pageTitles.contact,
      '/kontakt': t.pageTitles.contact,
      '/404': t.pageTitles.notFound
    };
    const normalizedPath =
      pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    const isInsightRoute =
      normalizedPath === '/insights' ||
      normalizedPath === '/blog' ||
      normalizedPath.startsWith('/insights/') ||
      normalizedPath.startsWith('/blog/');
    const isInsightArticle =
      normalizedPath.startsWith('/insights/') ||
      normalizedPath.startsWith('/blog/');
    const isNotFound = normalizedPath === '/404';
    const title = isInsightArticle
      ? t.pageTitles.insights
      : (pageTitles[normalizedPath] ?? pageTitles['/404']);
    const description =
      normalizedPath === '/'
        ? t.home.hero.description
        : isInsightRoute
          ? t.insights.hero.description
          : normalizedPath === '/about' || normalizedPath === '/o-nama'
            ? t.about.hero.description
            : t.footer.description;

    applySeoMetadata({
      canonicalUrl: createAbsoluteUrl(getCanonicalPathname(normalizedPath)),
      description,
      keywords: [
        'data engineering',
        'AI',
        'analytics',
        'business intelligence',
        'managed data platform'
      ],
      locale: openGraphLocaleByLanguage[language],
      robots: isNotFound
        ? 'noindex, follow'
        : 'index, follow, max-image-preview:large',
      title,
      type: 'website'
    });
  }, [language, pathname, t]);

  return null;
};

const RouteFallback = () => (
  <div className="min-h-screen bg-background-primary" aria-hidden="true" />
);

const AnimatedRoutes = () => {
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        className="min-h-screen"
        initial={
          prefersReducedMotion
            ? false
            : { opacity: 0, y: 14, filter: 'blur(4px)' }
        }
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        exit={
          prefersReducedMotion
            ? { opacity: 1 }
            : { opacity: 0, y: -10, filter: 'blur(4px)' }
        }
        transition={prefersReducedMotion ? { duration: 0 } : routeTransition}
      >
        <ScrollToTop />

        <Routes location={location}>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/services/data-engineering"
            element={<DataEngineeringPage />}
          />
          <Route
            path="/services/managed-data-platform"
            element={<ManagedDataPlatformPage />}
          />
          <Route
            path="/services/business-intelligence"
            element={<BusinessIntelligencePage />}
          />
          <Route
            path="/services/ai-machine-learning"
            element={<AiMachineLearningPage />}
          />
          <Route path="/cases" element={<CasesPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/insights/:slug" element={<InsightArticlePage />} />
          <Route path="/blog" element={<InsightsPage />} />
          <Route path="/blog/:slug" element={<InsightArticlePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/o-nama" element={<AboutPage />} />
          <Route path="/partneri" element={<PartnersPage />} />
          <Route path="/kontakt" element={<ContactPage />} />
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

export const AppRouter = () => {
  return (
    <BrowserRouter>
      <RouteSeo />
      <SameRouteScrollToTop />
      <AppNavbar />
      <Suspense fallback={<RouteFallback />}>
        <AnimatedRoutes />
      </Suspense>
      <BackToTopButton />
    </BrowserRouter>
  );
};
